import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, FormField } from '@heartinternet/hi-ui';
import { passwordIsStrong } from '../utils';

export interface ChangePasswordPayload {
  mailbox_id: string;
  mail_login_id: string;
  password: string;
}

export interface ChangePasswordModalProps {
  isOpen: boolean;
  mailboxId: string;
  mailLoginId: string;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: ChangePasswordPayload) => void;
}

/**
 * Change an assigned mailbox's password. Mirrors the legacy change-password modal +
 * manage.js validation (match, then strength); Confirm is disabled until both fields are
 * filled, and validation failures show as inline field errors.
 */
export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  mailboxId,
  mailLoginId,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [passwordError, setPasswordError] = useState<string | undefined>(undefined);
  const [confirmError, setConfirmError] = useState<string | undefined>(undefined);

  // The parent keeps this modal permanently mounted (only toggling isOpen), so the field
  // state would otherwise persist between openings — a cancelled attempt's password/errors
  // would reappear when Change password is opened for a different mailbox. Reset on close,
  // mirroring legacy manage.js which cleared the inputs each time the modal opened.
  /* eslint-disable react-hooks/set-state-in-effect -- reset the form when the modal closes */
  useEffect(() => {
    if (isOpen) return;
    setPassword('');
    setConfirm('');
    setPasswordError(undefined);
    setConfirmError(undefined);
  }, [isOpen]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const canSubmit = password !== '' && confirm !== '';

  const handleConfirm = () => {
    setPasswordError(undefined);
    setConfirmError(undefined);
    if (password !== confirm) {
      setConfirmError('Passwords must match!');
      return;
    }
    if (!passwordIsStrong(password)) {
      setPasswordError('Your new password is too weak.');
      return;
    }
    onSubmit({ mailbox_id: mailboxId, mail_login_id: mailLoginId, password });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Change password"
      size="medium"
      footer={
        <div className="hi-ui-be-modal-actions">
          <Button variant="tertiary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!canSubmit || submitting}
            loading={submitting}
          >
            Confirm
          </Button>
        </div>
      }
    >
      <div className="hi-ui-be-modal-fields">
        <FormField label="New Password" required error={passwordError}>
          <Input
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
          />
        </FormField>
        <FormField label="Confirm New Password" required error={confirmError}>
          <Input
            type="password"
            name="confirm_password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
          />
        </FormField>
      </div>
    </Modal>
  );
};
